import React from 'react';

import { useSelector } from 'react-redux';

import { ResponsiveBar } from 'cccisd-nivo/bar';

import style from './style.css';

const YOUTH_COLOR = '#806fa6';
const CAREGIVER_COLOR = '#e7a32a';

// Flattens scoreAverages (planName > timepoint > domain > pawnId) into per-domain
// averages for youth and caregiver, for the bar chart
const getChartData = scoreAverages => {
    const domainTotals = {};

    Object.values(scoreAverages).forEach(timepointData => {
        Object.values(timepointData).forEach(domainData => {
            Object.entries(domainData).forEach(([domain, pawnAverageData]) => {
                if (!domainTotals[domain]) {
                    domainTotals[domain] = {
                        youth: { sum: 0, count: 0 },
                        caregiver: { sum: 0, count: 0 },
                    };
                }
                Object.values(pawnAverageData).forEach(entry => {
                    const { role } = entry;
                    if (role === 'youth' || role === 'caregiver') {
                        domainTotals[domain][role].sum += entry.sum || 0;
                        domainTotals[domain][role].count += entry.numberOfQuestionsAnswered || 0;
                    }
                });
            });
        });
    });

    // set a value of -1 for the youth or caregiver domain average if there is no data at all from youths or caregivers in that domain
    return Object.entries(domainTotals).map(([domain, roles]) => ({
        domain,
        youth: roles.youth?.count > 0 ? +(roles.youth.sum / roles.youth.count).toFixed(2) : -1,
        caregiver: roles.caregiver?.count > 0 ? +(roles.caregiver.sum / roles.caregiver.count).toFixed(2) : -1,
    }));
};

const average = scores => {
    const res = scores.length > 0 ? +(scores.reduce((sum, score) => sum + score, 0) / scores.length).toFixed(2) : 0;

    return res;
};

const AggregateAverages = () => {
    const aggregateScoreAverages = useSelector(state => state.app.dashboard.aggregateScoreAverages);

    if (!aggregateScoreAverages) return <></>;

    const chartData = getChartData(aggregateScoreAverages);

    // y-axis always reaches at least 3.0; extends further if any bar exceeds it
    // ignore values of -1
    const chartMax = Math.max(3, ...chartData.flatMap(d => [d.youth, d.caregiver]).filter(v => v >= 0));

    // Overall averages used as dashed reference lines across all domains
    // ignore values of -1
    const youthAverage = average(
        chartData.map(domainAverageData => domainAverageData.youth).filter(averageValue => averageValue >= 0)
    );
    const caregiverAverage = average(
        chartData.map(domainAverageData => domainAverageData.caregiver).filter(averageValue => averageValue >= 0)
    );

    return (
        <div className={style.aggregate_graph}>
            <ResponsiveBar
                // filter out values of -1, which represent youths or carevivers not having any responses in a domain at all
                // also replace values of 0 with .05 so that the bar graph visually shows a tiny bar for a domain with 0 score
                data={chartData.map(domainData =>
                    Object.fromEntries(
                        Object.entries(domainData)
                            .filter(([, value]) => value !== -1)
                            .map(([domain, value]) => [domain, value === 0 ? 0.05 : value])
                    )
                )}
                keys={['youth', 'caregiver']}
                indexBy="domain"
                groupMode="grouped"
                colors={({ id }) => (id === 'youth' ? YOUTH_COLOR : CAREGIVER_COLOR)}
                labelTextColor="#ffffff"
                theme={{ labels: { text: { fontSize: 16 } } }}
                maxValue={chartMax}
                layers={[
                    'grid',
                    'axes',
                    'bars',
                    'markers',
                    'legends',
                    ({ bars }) => (
                        // show values of 0 when the data average is actually 0 from real responses
                        <>
                            {bars
                                .filter(bar => bar.data.value === 0.05)
                                .map(bar => {
                                    return (
                                        <text
                                            key={bar.key}
                                            x={bar.x + bar.width / 2}
                                            y={bar.y - 6}
                                            textAnchor="middle"
                                            style={{
                                                fontSize: 14,
                                                fontWeight: 'bold',
                                                fontStyle: 'italic',
                                                fill:
                                                    (bar.id ?? bar.data?.id) === 'youth'
                                                        ? YOUTH_COLOR
                                                        : CAREGIVER_COLOR,
                                            }}
                                        >
                                            0
                                        </text>
                                    );
                                })}
                        </>
                    ),
                ]}
                markers={[
                    {
                        axis: 'y',
                        value: youthAverage,
                        lineStyle: { stroke: YOUTH_COLOR, strokeWidth: 2, strokeDasharray: '4 4' },
                    },
                    {
                        axis: 'y',
                        value: caregiverAverage,
                        lineStyle: {
                            stroke: CAREGIVER_COLOR,
                            strokeWidth: 2,
                            strokeDasharray: '4 4',
                        },
                    },
                ]}
                margin={{ top: 10, right: 20, bottom: 80, left: 50 }}
                axisBottom={{ legend: 'Domain', legendPosition: 'middle', legendOffset: 40 }}
                axisLeft={{ legend: 'Average Score', legendPosition: 'middle', legendOffset: -40 }}
                legends={[
                    {
                        dataFrom: 'keys',
                        anchor: 'bottom',
                        direction: 'row',
                        justify: false,
                        translateX: 25,
                        translateY: 70,
                        itemsSpacing: 20,
                        itemWidth: 100,
                        itemHeight: 20,
                        itemDirection: 'left-to-right',
                        itemOpacity: 0.85,
                        symbolSize: 20,
                    },
                ]}
            />
        </div>
    );
};

export default AggregateAverages;
